// R10-1 — THE TWO HUMAN DESIGN REPORT VIEWS, AND THE IDENTITY BLOCK NOBODY COULD CALL.
//
// Third and fourth shells out of App.tsx after R9's pair, and they are here for the reason R9 was:
// **W12-5 had to buy a live Cauldron generation and drive a real browser to discover that a report
// shell printed "DATE OF BIRTH" with nothing under it.** These two carry the same shape — the place
// line is conditional, the name and the birthday lines never were — and no instrument on this site
// could reach them, because a component inside a 15,000-line TSX file cannot be imported by a .mjs
// gate. Out here `scripts/r10-report-views.mjs` CALLS them and reads the returned tree.
//
// Moved VERBATIM. The two types come with them (nothing else in the app names them), and both
// dependencies were already importable: `reportSectionCard` went out with R9-5 and the logo asset is
// one the extraction loader stubs.

import { reportSectionCard } from "./ReportView";
import type { NarrativeSection } from "./NarrativeReportView";
import menuLogoUrl from "../assets/menu-logo.webp";

export type HumanDesignReportData = {
  type: "human-design";
  header: { name: string; dateText: string; timeText: string; place: string };
  svg: string;
  chips: {
    type: string; strategy: string; authority: string; profile: string;
    definition: string; incarnationCross: string; notSelfTheme: string; signatureTheme: string;
  };
  numbers?: Array<{ label: string; value: string }>;
  centers: Array<{ name: string; defined: boolean; gates: number[]; interpretation: string }>;
  channels: Array<{ label: string; name: string; interpretation: string }>;
  overview: string;
  sections: NarrativeSection[];
};

export type HumanDesignCompatData = {
  type: "human-design-compat";
  header: { name: string; subtitle?: string; dateText?: string; timeText?: string; place?: string };
  names: { a: string; b: string };
  score: number | null;
  connectionType: string;
  patterns: string[];
  composite: Array<{ label: string; name: string }>;
  electromagnetic: Array<{ label: string; name: string }>;
  compromise: string[];
  dominance: Array<{ center: string; dominant: string; pattern: string }>;
  people: Array<{ name: string; type: string; strategy: string; authority: string; profile: string; definition: string }>;
  numbers?: Array<{ label: string; value: string }>;
  overview: string;
  sections: NarrativeSection[];
};

export function HumanDesignView({ data }: { data: HumanDesignReportData }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  const chips = data.chips || ({} as HumanDesignReportData["chips"]);
  return (
    <article className="chart-report hd-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">Human Design Profile</p>
      </header>

      <section className="chart-report__info">
        <h3>General Information</h3>
        <div className="chart-report__info-grid">
          {/* R10-2 — A LABELLED FIELD IS NEVER PRINTED OVER NOTHING. Every one of these four lines
              used to be written the other way round: place was conditional, name and birthday were
              not. That is exactly the defect W12-5 photographed in the narrative shell, and it is a
              defect a fixture could not see until this file existed. Held now by r10-report-views. */}
          {data.header.name ? <p><span>Name</span>{data.header.name}</p> : null}
          {data.header.dateText ? <p><span>Date of Birth</span>{data.header.dateText}</p> : null}
          {data.header.timeText ? <p><span>Time of Birth</span>{data.header.timeText}</p> : null}
          {data.header.place && data.header.place !== "Unknown" ? (
            <p><span>Place of Birth</span>{data.header.place}</p>
          ) : null}
        </div>
      </section>

      <section className="hd-summary">
        {data.svg ? (
          <div className="hd-bodygraph" aria-label="Your bodygraph" dangerouslySetInnerHTML={{ __html: data.svg }} />
        ) : null}
        <div className="hd-chips">
          {chips.type ? <div className="hd-chip hd-chip--lead"><span>Type</span><strong>{chips.type}</strong></div> : null}
          {chips.strategy ? <div className="hd-chip"><span>Strategy</span><strong>{chips.strategy}</strong></div> : null}
          {chips.authority ? <div className="hd-chip"><span>Authority</span><strong>{chips.authority}</strong></div> : null}
          {chips.profile ? <div className="hd-chip"><span>Profile</span><strong>{chips.profile}</strong></div> : null}
          {chips.definition ? <div className="hd-chip"><span>Definition</span><strong>{chips.definition}</strong></div> : null}
          {chips.signatureTheme ? <div className="hd-chip"><span>Signature</span><strong>{chips.signatureTheme}</strong></div> : null}
          {chips.notSelfTheme ? <div className="hd-chip"><span>Not-Self</span><strong>{chips.notSelfTheme}</strong></div> : null}
          {chips.incarnationCross ? <div className="hd-chip hd-chip--wide"><span>Incarnation Cross</span><strong>{chips.incarnationCross}</strong></div> : null}
        </div>
      </section>

      {data.centers && data.centers.length ? (
        <section className="hd-centers-wrap">
          <h3>Your Centers</h3>
          <div className="hd-centers">
            {data.centers.map((c) => (
              <div key={c.name} className={`hd-center${c.defined ? " hd-center--defined" : ""}`}>
                <span className="hd-center__name">{c.name}</span>
                <span className="hd-center__state">{c.defined ? "Defined" : "Open"}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="chart-report__reading">
        <h3>Your Reading</h3>
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}
        <div className="chart-report__cards">
          {(data.sections || []).map((section, index) => (
            reportSectionCard(index, section.heading, section.body, { heading: true })
          ))}
        </div>
      </section>
    </article>
  );
}

export function HumanDesignCompatView({ data }: { data: HumanDesignCompatData }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  const score = data.score;
  const scorePct = typeof score === "number" ? Math.max(0, Math.min(100, score)) : null;
  return (
    <article className="chart-report hd-report hd-compat-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">Human Design Compatibility</p>
      </header>

      <section className="chart-report__info">
        <h3>{data.header.name}</h3>
        <div className="hd-compat-pair">
          {data.people.map((p) => (
            <div key={p.name} className="hd-compat-person">
              <strong>{p.name}</strong>
              <span>{[p.type, p.authority, p.profile].filter(Boolean).join(" · ")}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="hd-compat-summary">
        {scorePct != null ? (
          <div className="hd-compat-score">
            <div className="hd-compat-score__num">{score}<span>/100</span></div>
            <div className="hd-compat-score__bar"><span style={{ width: `${scorePct}%` }} /></div>
            <div className="hd-compat-score__label">Compatibility</div>
          </div>
        ) : null}
        <div className="hd-chips">
          {data.connectionType ? <div className="hd-chip hd-chip--lead"><span>Connection</span><strong>{data.connectionType}</strong></div> : null}
          {data.patterns.map((p) => (
            <div key={p} className="hd-chip"><span>Pattern</span><strong>{p}</strong></div>
          ))}
        </div>
      </section>

      <section className="chart-report__reading">
        <h3>Your Connection</h3>
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}
        <div className="chart-report__cards">
          {(data.sections || []).map((section, index) => (
            reportSectionCard(index, section.heading, section.body, { heading: true })
          ))}
        </div>
      </section>
    </article>
  );
}
